import { useRef, useState } from 'react';
import { z } from 'zod';
import { Avatar, Button, Input } from '@/components/ui';
import { useAuthStore } from '@/stores/authStore';
import { useProjectParticipants } from '@/hooks/useProjectViews';
import { useProposalPresentation } from '@/hooks/useProposalReviews';
import { profileImageUrl } from '@/hooks/useProfile';
import type { useProjectCommand } from '@/hooks/useProjectCommand';
import type { MilestoneView, ProjectView } from '@/types/current-project';
import { entityIdSchema } from '@/types/current-project';
import { milestoneBudget } from '@/lib/project-presentation';
import { pendingProjectCommand } from '@/services/projectCommands';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import {
  putSubmissionPresentation,
  submissionDetailsSchema,
} from '@/api/adapter/submissionPresentations';

const savedSchema = z.object({
  details: submissionDetailsSchema,
  submissionId: entityIdSchema.optional(),
});

export function SubmitWorkAction({
  project,
  milestone,
  command,
  canSubmit,
}: {
  project: ProjectView;
  milestone: MilestoneView;
  command: ReturnType<typeof useProjectCommand>;
  canSubmit: boolean;
}) {
  const { currency, workerRole } = useDaoConfig();
  const principal = useAuthStore((s) => s.session?.principalId);
  const key = `abako-milestone-submission:${principal}:${project.projectId}:${milestone.milestoneId}`;
  const [initial] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return {
        saved: raw ? savedSchema.parse(JSON.parse(raw)) : null,
        error: '',
      };
    } catch {
      return {
        saved: null,
        error:
          'The saved submission cannot be read. Check the project before retrying.',
      };
    }
  });
  const [saved, setSaved] = useState(initial.saved);
  const [showForm, setShowForm] = useState(!!initial.saved);
  const [documentation, setDocumentation] = useState(
    initial.saved?.details.documentation ?? ''
  );
  const [links, setLinks] = useState(initial.saved?.details.links ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initial.error);
  const [confirmed, setConfirmed] = useState(false);
  const running = useRef(false);
  const participants = useProjectParticipants(project.projectId);
  const proposal = project.proposals.find((p) =>
    p.milestones.some((m) => m.milestoneId === milestone.milestoneId)
  );
  const presentation = useProposalPresentation(
    project.projectId,
    proposal?.proposalId
  );
  const description = presentation.data?.milestones.find(
    (m) => m.key === milestone.definition.key
  )?.description;
  const workers =
    participants.data?.workers.filter((w) =>
      milestone.assignments.some((a) => a.worker === w.accountId)
    ) ?? [];
  const locked = busy || command.busy || command.blocked;

  async function handleSubmit() {
    if (
      !principal ||
      running.current ||
      initial.error ||
      (!canSubmit && !saved)
    )
      return;
    running.current = true;
    setBusy(true);
    setError('');
    const assertOwner = () => {
      if (useAuthStore.getState().session?.principalId !== principal)
        throw new Error(
          'Your account changed. Log in again to resume this submission.'
        );
    };
    try {
      assertOwner();
      const pending = pendingProjectCommand(principal, project.projectId);
      if (
        pending &&
        (pending.command.kind !== 'completion-submit' ||
          pending.command.milestoneId !== milestone.milestoneId)
      )
        throw new Error(
          'Another project request is pending. Resume it on its original milestone.'
        );
      let draft = saved ?? {
        details: submissionDetailsSchema.parse({
          documentation: documentation.trim() || null,
          links: links.trim() || null,
        }),
      };
      sessionStorage.setItem(key, JSON.stringify(draft));
      setSaved(draft);
      if (!draft.submissionId) {
        const result = await command.run(
          pending || command.pending
            ? null
            : {
                kind: 'completion-submit',
                projectId: project.projectId,
                milestoneId: milestone.milestoneId,
                // Supporting references are free text stored in the adapter.
                payload: { deliverable: null },
              }
        );
        assertOwner();
        if (!result) return;
        const submissionId = entityIdSchema.parse(
          result.receipt?.createdEntityId
        );
        draft = { ...draft, submissionId };
        sessionStorage.setItem(key, JSON.stringify(draft));
        setSaved(draft);
      }
      assertOwner();
      await putSubmissionPresentation(draft.submissionId!, draft.details);
      assertOwner();
      command.finishSubmission();
      sessionStorage.removeItem(key);
      setSaved(null);
      setShowForm(false);
      setConfirmed(true);
    } catch (reason) {
      if (useAuthStore.getState().session?.principalId === principal)
        setError(
          reason instanceof z.ZodError
            ? reason.issues.map((issue) => issue.message).join(' ')
            : reason instanceof Error
              ? reason.message
              : 'Submission failed. Retry the saved request.'
        );
    } finally {
      running.current = false;
      if (useAuthStore.getState().session?.principalId === principal)
        setBusy(false);
    }
  }

  if (!canSubmit && !saved && !initial.error)
    return confirmed ? (
      <p role="status">Milestone submitted for review.</p>
    ) : null;
  if (!showForm && !initial.error)
    return (
      <Button
        variant="primary"
        size="sm"
        disabled={locked || command.pending}
        onClick={() => setShowForm(true)}
        className="text-xs"
      >
        <i className="ri-upload-2-line mr-1" />
        Submit for Review
      </Button>
    );

  return (
    <div className="space-y-4 rounded-lg border border-[#3D3D3D] bg-[#231F1F] p-4">
      <div>
        <h4 className="mb-3 text-sm font-semibold text-[#F5F5F5]">
          Your Submission
        </h4>
        <div className="space-y-2 rounded-lg border border-[#3D3D3D] bg-[#1A1A1A] p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[#F5F5F5]">
              {milestone.definition.title}
            </span>
            <span className="text-xs font-medium text-[#36D399]">
              {milestoneBudget(milestone)} {currency.symbol}
            </span>
          </div>
          {description && (
            <p className="text-xs leading-relaxed text-[#9B9B9B]">
              {description}
            </p>
          )}
          {workers.map((worker) => (
            <div
              key={worker.accountId}
              className="flex items-center gap-2 text-xs text-[#9B9B9B]"
            >
              <Avatar
                size="sm"
                alt={
                  worker.profiles?.worker?.name ??
                  worker.displayName ??
                  workerRole
                }
                src={
                  worker.workerImage && worker.profiles
                    ? profileImageUrl(worker.profiles.principalId, 'worker')
                    : undefined
                }
              />
              <span>
                {worker.profiles?.worker?.name ??
                  worker.displayName ??
                  workerRole}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <label
          htmlFor={'documentation-' + milestone.milestoneId}
          className="mb-1.5 block text-xs font-medium text-[#9B9B9B]"
        >
          Documentation
        </label>
        <Input
          id={'documentation-' + milestone.milestoneId}
          value={documentation}
          onChange={(e) => setDocumentation(e.target.value)}
          disabled={locked || !!saved}
          placeholder="Provide a link to your documentation"
        />
      </div>
      <div>
        <label
          htmlFor={'links-' + milestone.milestoneId}
          className="mb-1.5 block text-xs font-medium text-[#9B9B9B]"
        >
          Other Links
        </label>
        <Input
          id={'links-' + milestone.milestoneId}
          value={links}
          onChange={(e) => setLinks(e.target.value)}
          disabled={locked || !!saved}
          placeholder="Add links to supporting documents"
        />
      </div>
      {saved?.submissionId && (
        <p role="status" className="text-xs text-[#9B9B9B]">
          The delivery is confirmed. Retry saving its links; this will not send
          another delivery.
        </p>
      )}
      <div className="flex gap-2">
        <Button
          variant="primary"
          onClick={() => void handleSubmit()}
          isLoading={busy || command.busy}
          disabled={locked || !!initial.error}
          className="flex-1"
        >
          {saved ? 'Retry saved submission' : 'Submit Milestone'}
        </Button>
        {!saved?.submissionId && !command.pending && (
          <Button
            variant="ghost"
            disabled={locked}
            onClick={() => {
              sessionStorage.removeItem(key);
              setSaved(null);
              setShowForm(false);
            }}
            className="flex-1"
          >
            Cancel
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="text-xs text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
